import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Drawer,
  IconButton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from "@mui/icons-material";

import {
  getAdminConfig,
  saveAdminConfig,
  updateAdminConfig,
  deleteAdminConfig,
} from "../../services/webServices";

const JiraConnectionFields = ({
  jiraUrl,
  setJiraUrl,
  jiraUser,
  setJiraUser,
  jiraApiToken,
  setJiraApiToken,
  connectionName,
  setConnectionName,
  setMessage,
  onSave,
  onCancel,
  isEditing,
  config_id,
}) => {
  const handleJiraSave = async () => {
    try {
      const configuration = {
        jiraUrl: jiraUrl.trim().replace(/^\/|\/$/g, ""),
        jiraUser,
        jiraApiToken,
        connectionName,
      };

      const response = isEditing
        ? await updateAdminConfig({
            config_id:parseInt(config_id),
            tool: 'Jira',
            configuration,
            default_configuration: false,
          })
        : await saveAdminConfig({
            tool: 'Jira',
            configuration,
            default_configuration: false,
          });

      setMessage(response.data.message || "JIRA Configuration saved successfully.");
      onSave();
    } catch (error) {
      if (error.response?.status === 401) {
        setMessage("Invalid Jira API credentials. Please verify your Jira API key and URL.");
      } else if (error.response?.status === 502) {
        setMessage("Network failure: Unable to connect to the Jira server. Please check your network or Jira URL.");
      } else {
        setMessage(error.response?.data?.message || "An unexpected error occurred. Please try again.");
      }
    }
  };

  return (
    <>
      <TextField
        fullWidth
        label="URL*"
        variant="outlined"
        margin="normal"
        placeholder="https://your-domain.atlassian.net"
        value={jiraUrl}
        onChange={(e) => setJiraUrl(e.target.value)}
      />
      
      <TextField
        fullWidth
        label="User*"
        variant="outlined"
        margin="normal"
        placeholder="Enter username"
        value={jiraUser}
        onChange={(e) => setJiraUser(e.target.value)}
      />
      
      <TextField
        fullWidth
        label="API Token*"
        variant="outlined"
        margin="normal"
        placeholder="Enter API token"
        type="password"
        value={jiraApiToken}
        onChange={(e) => setJiraApiToken(e.target.value)}
      />

      <TextField
        fullWidth
        label="Save connection as"
        variant="outlined"
        margin="normal"
        placeholder="Enter name"
        value={connectionName}
        onChange={(e) => setConnectionName(e.target.value)}
        sx={{ mb: 3 }}
        error={connectionName.length > 0 && connectionName.length < 4}
        helperText={
        connectionName.length > 0 && connectionName.length < 4
        ? "Connection name must be at least 4 characters"
        : ""
        }
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 22 }}>
        <Button
          variant="outlined"
          sx={{ 
            textTransform: "none",
            color: "#49A3FF",
            borderColor: "#49A3FF"
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ 
            textTransform: "none",
            bgcolor: "#49A3FF",
            "&:hover": { bgcolor: "#3691E6" }
          }}
          disabled={!jiraUrl || !jiraUser || !jiraApiToken || (connectionName.length > 0 && connectionName.length < 4)}
          onClick={handleJiraSave}
        >
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </Box>
    </>
  );
};

const AzureConnectionFields = ({ 
  url, 
  setUrl, 
  pat, 
  setPat, 
  connectionName,
  setConnectionName,
  setMessage, 
  onSave,
  onCancel,
  isEditing,
  config_id,
}) => {
  const handleAzureSave = async () => {
    try {
      const configuration = {
        adoUrl: url.trim().replace(/^\/|\/$/g, ""),
        adoPat: pat,
        connectionName,
      };

      const response = isEditing
        ? await updateAdminConfig({
            config_id:parseInt(config_id),
            tool: 'Azure',
            configuration,
            default_configuration: false,
          })
        : await saveAdminConfig({
            tool: 'Azure',
            configuration,
            default_configuration: false,
          });

      setMessage(response.data.message || "Azure configuration saved successfully");
      onSave();
    } catch (error) {
      setMessage(error.response?.data?.message || "Internal server error");
    }
  };

  return (
    <>
      <TextField
        fullWidth
        label="URL*"
        variant="outlined"
        margin="normal"
        placeholder="https://dev.azure.com/your-org"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      
      <TextField
        fullWidth
        label="Personal Authentication Token*"
        variant="outlined"
        margin="normal"
        placeholder="Enter Personal Authentication Token"
        type="password"
        value={pat}
        onChange={(e) => setPat(e.target.value)}
      />
     
      <TextField
        fullWidth
        label="Save connection as"
        variant="outlined"
        margin="normal"
        placeholder="Enter name"
        value={connectionName}
        onChange={(e) => setConnectionName(e.target.value)}
        sx={{ mb: 3 }}
error={connectionName.length > 0 && connectionName.length < 4}
        helperText={
        connectionName.length > 0 && connectionName.length < 4
        ? "Connection name must be at least 4 characters"
        : ""
  }
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 31 }}>
        <Button
          variant="outlined"
          sx={{ 
            textTransform: "none",
            color: "#49A3FF",
            borderColor: "#49A3FF"
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ 
            textTransform: "none",
            bgcolor: "#49A3FF",
            "&:hover": { bgcolor: "#3691E6" }
          }}
          disabled={!url || !pat || (connectionName.length > 0 && connectionName.length < 4)}
          onClick={handleAzureSave}
        >
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </Box>
    </>
  );
};

const QtestConnectionFields = ({
  qtest_url,
  setQtest_url,
  qtest_pat,
  setQtest_pat,
  connectionName,
  setConnectionName,
  setMessage,
  onSave,
  onCancel,
  isEditing,
  config_id,
}) => {
  const handleQtestSave = async () => {
    try {
      const configuration = {
        qtest_url:qtest_url.trim().replace(/^\/|\/$/g, ""),
        qtest_pat,
        connectionName,
      };

      const response = isEditing
        ? await updateAdminConfig({
            config_id:parseInt(config_id),
            tool: 'qTest',
            configuration,
            default_configuration: false,
          })
        : await saveAdminConfig({
            tool: 'qTest',
            configuration,
            default_configuration: false,
          });

      setMessage(response.data.message || "qTest configuration saved successfully");
      onSave();
    } catch (error) {
      setMessage(error.response?.data?.message || "Internal server error");
    }
  };

  return (
    <>
      <TextField
        fullWidth
        label="URL*"
        variant="outlined"
        margin="normal"
        placeholder="https://your-domain.qtestnet.com"
        value={qtest_url}
        onChange={(e) => setQtest_url(e.target.value)}
      />
      
      <TextField
        fullWidth
        label="Personal Authentication Token*"
        variant="outlined"
        margin="normal"
        placeholder="Enter Personal Authentication Token"
        type="password"
        value={qtest_pat}
        onChange={(e) => setQtest_pat(e.target.value)}
      />

      <TextField
        fullWidth
        label="Save connection as"
        variant="outlined"
        margin="normal"
        placeholder="Enter name"
        value={connectionName}
        onChange={(e) => setConnectionName(e.target.value)}
        sx={{ mb: 3 }}
        error={connectionName.length > 0 && connectionName.length < 4}
        helperText={
        connectionName.length > 0 && connectionName.length < 4
        ? "Connection name must be at least 4 characters"
        : ""
        }
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 31 }}>
        <Button
          variant="outlined"
          sx={{ 
            textTransform: "none",
            color: "#49A3FF",
            borderColor: "#49A3FF"
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ 
            textTransform: "none",
            bgcolor: "#49A3FF",
            "&:hover": { bgcolor: "#3691E6" }
          }}
          disabled={!qtest_url || !qtest_pat || (connectionName.length > 0 && connectionName.length < 4)}
          onClick={handleQtestSave}
        >
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </Box>
    </>
  );
};

const TestRailConnectionFields = ({
  testRailUrl,
  setTestRailUrl,
  testRailUser,
  setTestRailUser,
  testRailToken,
  setTestRailToken,
  connectionName,
  setConnectionName,
  setMessage,
  onSave,
  onCancel,
  isEditing,
  config_id,
}) => {
  const handleTestRailSave = async () => {
    try {
      const configuration = {
        testRailUrl:testRailUrl.trim().replace(/^\/|\/$/g, ""),
        testRailUser,
        testRailToken,
        connectionName,
      };

      const response = isEditing
        ? await updateAdminConfig({
            config_id:parseInt(config_id),
            tool: 'TestRail',
            configuration,
            default_configuration: false,
          })
        : await saveAdminConfig({
            tool: 'TestRail',
            configuration,
            default_configuration: false,
          });

      setMessage(response.data.message || "TestRail Configuration saved successfully.");
      onSave();
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to save TestRail configuration.");
      console.error(error);
    }
  };

  return (
    <>
      <TextField
        fullWidth
        label="URL*"
        variant="outlined"
        margin="normal"
        placeholder="https://your-domain.testrail.io"
        value={testRailUrl}
        onChange={(e) => setTestRailUrl(e.target.value)}
      />
      
      <TextField
        fullWidth
        label="User*"
        variant="outlined"
        margin="normal"
        placeholder="Enter username"
        value={testRailUser}
        onChange={(e) => setTestRailUser(e.target.value)}
      />
      
      <TextField
        fullWidth
        label="API Token*"
        variant="outlined"
        margin="normal"
        placeholder="Enter API Token"
        type="password"
        value={testRailToken}
        onChange={(e) => setTestRailToken(e.target.value)}
      />

      <TextField
        fullWidth
        label="Save connection as"
        variant="outlined"
        margin="normal"
        placeholder="Enter name"
        value={connectionName}
        onChange={(e) => setConnectionName(e.target.value)}
        sx={{ mb: 3 }}
        error={connectionName.length > 0 && connectionName.length < 4}
        helperText={
        connectionName.length > 0 && connectionName.length < 4
        ? "Connection name must be at least 4 characters"
        : ""
        }
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 21 }}>
        <Button
          variant="outlined"
          sx={{ 
            textTransform: "none",
            color: "#49A3FF",
            borderColor: "#49A3FF"
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ 
            textTransform: "none",
            bgcolor: "#49A3FF",
            "&:hover": { bgcolor: "#3691E6" }
          }}
          disabled={!testRailUrl || !testRailUser || !testRailToken || (connectionName.length > 0 && connectionName.length < 4)}
          onClick={handleTestRailSave}
        >
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </Box>
    </>
  );
};

const ConfluenceConnectionFields = ({
  confluenceUrl,
  setConfluenceUrl,
  confluenceUser,
  setConfluenceUser,
  confluenceToken,
  setConfluenceToken,
  connectionName,
  setConnectionName,
  setMessage,
  onSave,
  onCancel,
  isEditing,
  config_id,
}) => {
  const handleConfluenceSave = async () => {
    try {
      const configuration = {
        confluenceUrl:confluenceUrl.trim().replace(/^\/|\/$/g, ""),
        confluenceUser,
        confluenceToken,
        connectionName,
      };

      const response = isEditing
        ? await updateAdminConfig({
            config_id:parseInt(config_id),
            tool: 'Confluence',
            configuration,
            default_configuration: false,
          })
        : await saveAdminConfig({
            tool: 'Confluence',
            configuration,
            default_configuration: false,
          });

      setMessage(response.data.message || "Confluence configuration saved successfully.");
      onSave();
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to save Confluence configuration.");
      console.error(error);
    }
  };

  return (
    <>
      <TextField
        fullWidth
        label="URL*"
        variant="outlined"
        margin="normal"
        placeholder="https://your-domain.atlassian.net/wiki"
        value={confluenceUrl}
        onChange={(e) => setConfluenceUrl(e.target.value)}
      />
      
      <TextField
        fullWidth
        label="User*"
        variant="outlined"
        margin="normal"
        placeholder="Enter username"
        value={confluenceUser}
        onChange={(e) => setConfluenceUser(e.target.value)}
      />
      
      <TextField
        fullWidth
        label="API Token*"
        variant="outlined"
        margin="normal"
        placeholder="Enter API Token"
        type="password"
        value={confluenceToken}
        onChange={(e) => setConfluenceToken(e.target.value)}
      />

      <TextField
        fullWidth
        label="Save connection as"
        variant="outlined"
        margin="normal"
        placeholder="Enter name"
        value={connectionName}
        onChange={(e) => setConnectionName(e.target.value)}
        sx={{ mb: 3 }}
        error={connectionName.length > 0 && connectionName.length < 4}
        helperText={
        connectionName.length > 0 && connectionName.length < 4
        ? "Connection name must be at least 4 characters"
        : ""
       }
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 21 }}>
        <Button
          variant="outlined"
          sx={{ 
            textTransform: "none",
            color: "#49A3FF",
            borderColor: "#49A3FF"
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ 
            textTransform: "none",
            bgcolor: "#49A3FF",
            "&:hover": { bgcolor: "#3691E6" }
          }}
          disabled={!confluenceUrl || !confluenceUser || !confluenceToken || (connectionName.length > 0 && connectionName.length < 4)}
          onClick={handleConfluenceSave}
        >
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </Box>
    </>
  );
};

const AccountSettings = ({ open, onClose,onCancel, selectedApp, editMode = false, existingConfig = null }) => {
  const [message, setMessage] = useState("");
  const [isEditing, setIsEditing] = useState(editMode);
  const [configId, setConfigId] = useState(null);

  const [connectionName, setConnectionName] = useState("");
  
  const [jiraUrl, setJiraUrl] = useState("");
  const [jiraUser, setJiraUser] = useState("");
  const [jiraApiToken, setJiraApiToken] = useState("");
  
  const [adoUrl, setAdoUrl] = useState("");
  const [adoPat, setAdoPat] = useState("");
  
  const [qtest_url, setQtest_url] = useState("");
  const [qtest_pat, setQtest_pat] = useState("");
  
  const [testRailUrl, setTestRailUrl] = useState("");
  const [testRailUser, setTestRailUser] = useState("");
  const [testRailToken, setTestRailToken] = useState("");
  
  const [confluenceUrl, setConfluenceUrl] = useState("");
  const [confluenceUser, setConfluenceUser] = useState("");
  const [confluenceToken, setConfluenceToken] = useState("");

  useEffect(() => {
    if (editMode && existingConfig) {
          console.log("ExistingConfig:", existingConfig);

      populateFields(existingConfig);
      setIsEditing(true);
    } else if (!editMode) {
      resetForm();
      setIsEditing(false);
    }
  }, [selectedApp, editMode, existingConfig, open]);

  const loadExistingConfig = async () => {
    try {
      const response = await getAdminConfig({ tool: selectedApp });
      if (response.data) {
        populateFields(response.data.configuration);
        setIsEditing(true);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error loading existing config:', error);
      }
    }
  };

  const populateFields = (config) => {
    const configuration = config.configuration || {};

    resetForm();
    setConfigId(config.config_id);
    setConnectionName(configuration.connectionName || "");


    switch (selectedApp) {
      case "Jira":
        setJiraUrl(configuration.jiraUrl || "");
        setJiraUser(configuration.jiraUser || "");
        setJiraApiToken(configuration.jiraApiToken || "");
        break;
      case "Azure":
        setAdoUrl(configuration.adoUrl || "");
        setAdoPat(configuration.adoPat || "");
        break;
      case "qTest":
        setQtest_url(configuration.qtest_url || "");
        setQtest_pat(configuration.qtest_pat || "");
        break;
      case "TestRail":
        setTestRailUrl(configuration.testRailUrl || "");
        setTestRailUser(configuration.testRailUser || "");
        setTestRailToken(configuration.testRailToken || "");
        break;
      case "Confluence":
        setConfluenceUrl(configuration.confluenceUrl || "");
        setConfluenceUser(configuration.confluenceUser || "");
        setConfluenceToken(configuration.confluenceToken || "");
        break;
    }
    setConnectionName(configuration.connectionName || "");
  };

  const handleSaveConnection = () => {
    onClose();
    resetForm();
  };

  const handleCancelConnection = () => {
  onCancel();
  resetForm();
};


  const handleDeleteConnection = async () => {
    try {
      await deleteAdminConfig({ tool: selectedApp });
      setMessage(`${selectedApp} configuration deleted successfully.`);
      resetForm();
      setIsEditing(false);
      onClose();
    } catch (error) {
      setMessage(error.response?.data?.message || `Failed to delete ${selectedApp} configuration.`);
    }
  };

  const resetForm = () => {
    setConnectionName("");
    if (!isEditing) {
      setConfigId(null);
    }    
    setJiraUrl("");
    setJiraUser("");
    setJiraApiToken("");
    setAdoUrl("");
    setAdoPat("");
    setQtest_url("");
    setQtest_pat("");
    setTestRailUrl("");
    setTestRailUser("");
    setTestRailToken("");
    setConfluenceUrl("");
    setConfluenceUser("");
    setConfluenceToken("");
    setMessage("");
  };

  const renderConnectionFields = () => {
    const commonProps = {
      setMessage,
      onSave: handleSaveConnection,
      onCancel: handleCancelConnection,
      isEditing,
      config_id:configId,
      connectionName,
      setConnectionName,
    };

    switch (selectedApp) {
      case "Jira":
        return (
          <JiraConnectionFields
            jiraUrl={jiraUrl}
            setJiraUrl={setJiraUrl}
            jiraUser={jiraUser}
            setJiraUser={setJiraUser}
            jiraApiToken={jiraApiToken}
            setJiraApiToken={setJiraApiToken}
            {...commonProps}
          />
        );
      case "Azure":
        return (
          <AzureConnectionFields
            url={adoUrl}
            setUrl={setAdoUrl}
            pat={adoPat}
            setPat={setAdoPat}
            {...commonProps}
          />
        );
      case "qTest":
        return (
          <QtestConnectionFields
            qtest_url={qtest_url}
            setQtest_url={setQtest_url}
            qtest_pat={qtest_pat}
            setQtest_pat={setQtest_pat}
            {...commonProps}
          />
        );
      case "TestRail":
        return (
          <TestRailConnectionFields
            testRailUrl={testRailUrl}
            setTestRailUrl={setTestRailUrl}
            testRailUser={testRailUser}
            setTestRailUser={setTestRailUser}
            testRailToken={testRailToken}
            setTestRailToken={setTestRailToken}
            {...commonProps}
          />
        );
      case "Confluence":
        return (
          <ConfluenceConnectionFields
            confluenceUrl={confluenceUrl}
            setConfluenceUrl={setConfluenceUrl}
            confluenceUser={confluenceUser}
            setConfluenceUser={setConfluenceUser}
            confluenceToken={confluenceToken}
            setConfluenceToken={setConfluenceToken}
            {...commonProps}
          />
        );
      default:
        return (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography>
              Configuration not available for {selectedApp}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 540,
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #e0e0e0",
            bgcolor: "white",
          }}
        >
          <IconButton onClick={onClose} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {isEditing ? `Edit ${selectedApp} connection` : `Add ${selectedApp} connection`}
          </Typography>
         
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
          {message && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body2" color={message.includes('successfully') ? 'success.main' : 'error.main'}>
                {message}
              </Typography>
            </Box>
          )}
          {renderConnectionFields()}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AccountSettings;